import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import SettingsIcon from "lucide-react/dist/esm/icons/settings";
import Layers3Icon from "lucide-react/dist/esm/icons/layers-3";
import { Button } from "@quake/shared-ui/components/ui/button";
import { toast } from "@quake/shared-ui/lib/sonner";
import { useDebounce } from "@quake/shared-ui/hooks/useDebounce";
import { z } from "zod";
import { trpc } from "~/utils/trpc";
import { useBreadCrumbs } from "~/contexts/useBreadCrumbs";
import { SkillsReviewAlert } from "./-components/skills-review-alert";
import { TaxonomyPanel } from "./-components/taxonomy-panel";

export const Route = createFileRoute("/_dashboard/$datasetSlug/data/skills/")({
  validateSearch: z.object({
    tab: z.enum(["taxonomy", "graph", "catalog"]).optional(),
  }),
  beforeLoad: ({ params, search }) => {
    if (search.tab === "catalog") {
      throw redirect({ to: "/$datasetSlug/data/skills/settings", params: { datasetSlug: params.datasetSlug } });
    }
  },
  component: SkillsRouteComponent,
});

function SkillsRouteComponent() {
  const { datasetSlug } = Route.useParams();
  useBreadCrumbs([{ title: "Skills", link: `/${datasetSlug}/data/skills` }]);
  return <SkillsPage />;
}

function SkillsPage() {
  const qc = useQueryClient();
  const { datasetSlug } = Route.useParams();
  const metaQuery = useQuery(trpc.skills.getCatalogMeta.queryOptions());
  const reviewSummaryQuery = useQuery(trpc.skills.getCandidateSkillReviewSummary.queryOptions());

  const onSuccess = invalidateWorkspace(qc);
  const createSkill = useMutation(trpc.skills.createSkill.mutationOptions({ onSuccess }));

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set());
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set(["active"]));
  const [selectedPresetIds, setSelectedPresetIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [newSkillName, setNewSkillName] = useState("");
  const [newSkillCategoryId, setNewSkillCategoryId] = useState("");

  const skillsQuery = useQuery(
    trpc.skills.listSkills.queryOptions(
      {
        search: debouncedSearch,
        categoryIds: [...selectedCategoryIds],
        statuses: [...selectedStatuses],
        presetIds: [...selectedPresetIds],
        pagination: { page, pageSize: 50 },
      },
      { placeholderData: (previousData) => previousData },
    ),
  );

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, selectedCategoryIds, selectedStatuses, selectedPresetIds]);

  if (metaQuery.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading skills workspace…</div>;
  }

  const meta = metaQuery.data;
  if (!meta) {
    return <div className="p-6 text-sm text-muted-foreground">Skills workspace unavailable.</div>;
  }

  const runQuickCreateSkill = async () => {
    if (!newSkillName.trim()) return;
    if (!newSkillCategoryId) {
      toast.error("Select a skill category to create a skill.");
      return;
    }
    await createSkill.mutateAsync({
      name: newSkillName.trim(),
      status: "active",
      skill_category_id: newSkillCategoryId,
    });
    setNewSkillName("");
  };

  return (
    <div className="m-5 flex h-[calc(100vh-6.5rem)] flex-col gap-4">
      <div className="flex items-start justify-between gap-6">
        <div className="max-w-3xl">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold">Skills</h1>
            <Link to="/$datasetSlug/data/skills/settings" params={{ datasetSlug }} aria-label="Open skills settings">
              <SettingsIcon className="w-4 h-4 ml-2 text-muted-foreground transition hover:rotate-90" />
            </Link>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Curate the shared skills taxonomy, aliases, and skill sets your team uses across the dataset.
          </p>
        </div>
        <Button asChild variant="outline" className="rounded-full">
          <Link to="/$datasetSlug/data/skills/sets" params={{ datasetSlug }}>
            <Layers3Icon className="mr-2 size-4" />
            Skill sets
          </Link>
        </Button>
      </div>

      <SkillsReviewAlert
        datasetSlug={datasetSlug}
        hasPendingReview={(reviewSummaryQuery.data?.pendingGroupCount ?? 0) > 0}
        pendingGroupCount={reviewSummaryQuery.data?.pendingGroupCount}
      />

      <div className="min-h-0 flex-1 overflow-hidden rounded-[24px] border border-black/5 bg-white/70 dark:border-white/10 dark:bg-white/[0.03]">
        <div className="h-full min-h-0">
          <TaxonomyPanel
            datasetSlug={datasetSlug}
            skillCategories={meta.skillCategories}
            presets={meta.presets}
            skills={skillsQuery.data?.data ?? []}
            total={skillsQuery.data?.total ?? 0}
            page={skillsQuery.data?.page ?? page}
            pageSize={skillsQuery.data?.pageSize ?? 50}
            totalPages={skillsQuery.data?.totalPages ?? 0}
            facets={skillsQuery.data?.facets}
            isFetching={skillsQuery.isFetching}
            search={search}
            setSearch={setSearch}
            selectedCategoryIds={selectedCategoryIds}
            setSelectedCategoryIds={setSelectedCategoryIds}
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            selectedPresetIds={selectedPresetIds}
            setSelectedPresetIds={setSelectedPresetIds}
            newSkillName={newSkillName}
            setNewSkillName={setNewSkillName}
            newSkillCategoryId={newSkillCategoryId}
            setNewSkillCategoryId={setNewSkillCategoryId}
            onCreateSkill={runQuickCreateSkill}
            onPageChange={setPage}
          />
        </div>
      </div>
    </div>
  );
}

function invalidateWorkspace(qc: ReturnType<typeof useQueryClient>) {
  return async () => {
    await Promise.all([
      qc.invalidateQueries(trpc.skills.getCatalogMeta.queryFilter()),
      qc.invalidateQueries(trpc.skills.listSkills.queryFilter()),
      qc.invalidateQueries(trpc.skills.searchSkillOptions.queryFilter()),
      qc.invalidateQueries(trpc.skills.getCandidateSkillReviewSummary.queryFilter()),
    ]);
  };
}
